
import { Helmet } from "react-helmet";
import { safeJsonStringify } from "@/lib/utils";

interface LocalBusinessSchemaProps {
  name: string;
  description: string;
  url: string;
  logo: string;
  image?: string;
  telephone?: string;
  email?: string;
  priceRange?: string;
  foundingDate?: string;
  naics?: string; // North American Industry Classification System code
  address?: {
    streetAddress: string;
    addressLocality: string;
    addressRegion: string;
    postalCode: string;
    addressCountry: string;
  };
  geo?: {
    latitude: number;
    longitude: number;
  };
  openingHours?: {
    dayOfWeek: string;
    opens: string;
    closes: string;
  }[];
  areaServed?: string | string[];
  sameAs?: string[];
  services?: Array<{
    name: string;
    description: string;
    url?: string;
  }>;
}

export const LocalBusinessSchema = ({
  name,
  description,
  url,
  logo,
  image,
  telephone,
  email,
  priceRange,
  address,
  geo,
  openingHours,
  areaServed,
  foundingDate,
  naics,
  sameAs = [],
  services = []
}: LocalBusinessSchemaProps) => {
  // Format URL and images
  const formattedUrl = url.startsWith('http') ? url : `https://edgefi.com${url}`;
  const formattedLogo = logo.startsWith('http') ? logo : `https://edgefi.com${logo}`;
  
  // Build schema
  const schema: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": name,
    "description": description,
    "url": formattedUrl,
    "logo": formattedLogo,
    "image": image ? (image.startsWith('http') ? image : `https://edgefi.com${image}`) : formattedLogo
  };
  
  // Add optional fields if provided
  if (telephone) schema.telephone = telephone;
  if (email) schema.email = email;
  if (priceRange) schema.priceRange = priceRange;
  if (foundingDate) schema.foundingDate = foundingDate;
  if (naics) schema.naics = naics;
  
  if (address) {
    schema.address = {
      "@type": "PostalAddress",
      ...address
    };
  }
  
  if (geo) {
    schema.geo = {
      "@type": "GeoCoordinates",
      "latitude": geo.latitude,
      "longitude": geo.longitude
    };
  }
  
  if (openingHours && openingHours.length > 0) {
    schema.openingHoursSpecification = openingHours.map(hours => ({
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": hours.dayOfWeek,
      "opens": hours.opens,
      "closes": hours.closes
    }));
  }
  
  if (areaServed) {
    if (Array.isArray(areaServed)) {
      schema.areaServed = areaServed.map(area => ({
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": geo?.latitude || 0,
          "longitude": geo?.longitude || 0
        },
        "geoRadius": "50000" // 50km radius, adjust as needed
      }));
    } else {
      schema.areaServed = areaServed;
    }
  }
  
  if (sameAs && sameAs.length > 0) {
    schema.sameAs = sameAs;
  }
  
  if (services && services.length > 0) {
    schema.hasOfferCatalog = {
      "@type": "OfferCatalog",
      "name": "Services",
      "itemListElement": services.map((service, index) => ({
        "@type": "Service",
        "name": service.name,
        "description": service.description,
        "url": service.url || `${formattedUrl}/services/#${service.name.toLowerCase().replace(/\s+/g, '-')}`,
        "provider": {
          "@type": "Organization",
          "name": name
        }
      }))
    };
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {safeJsonStringify(schema)}
      </script>
    </Helmet>
  );
};
